import { useState, useEffect, useCallback } from 'react';
import { client } from '../../api/client';
import { useToast } from '../common/Toast';

interface SupportRequest {
  id: number;
  userId: number | null;
  userName: string;
  userEmail: string;
  message: string;
  chatHistory: string | null;
  page: string | null;
  status: 'new' | 'in_progress' | 'resolved' | 'closed';
  adminId: number | null;
  adminName: string | null;
  adminReply: string | null;
  repliedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

interface StatusCounts {
  new: number;
  in_progress: number;
  resolved: number;
  closed: number;
}

const statusLabels: Record<string, string> = {
  new: 'Novi',
  in_progress: 'U toku',
  resolved: 'Rešeni',
  closed: 'Zatvoreni',
};

const statusColors: Record<string, string> = {
  new: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  in_progress: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  closed: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-400',
};

export function SupportRequestsTab() {
  const toast = useToast();
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [counts, setCounts] = useState<StatusCounts>({ new: 0, in_progress: 0, resolved: 0, closed: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState<SupportRequest | null>(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (searchTerm) params.set('search', searchTerm);
      
      const response = await client.api.fetch(`/api/admin/support-requests?${params.toString()}`);
      const data = await response.json();
      if (data.success) {
        setRequests(data.data);
        setCounts(data.counts);
      }
    } catch (error) {
      console.error('Failed to fetch support requests:', error);
      toast.error('Greška', 'Nije moguće učitati zahteve za podršku');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleUpdateStatus = async (id: number, status: string) => {
    try {
      const response = await client.api.fetch(`/api/admin/support-requests/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Uspešno', `Status promenjen na: ${statusLabels[status]}`);
        fetchRequests();
        if (selectedRequest?.id === id) {
          setSelectedRequest(prev => prev ? { ...prev, status: status as SupportRequest['status'] } : null);
        }
      }
    } catch (error) {
      toast.error('Greška', 'Nije moguće promeniti status');
    }
  };

  const handleSendReply = async () => {
    if (!selectedRequest || !replyText.trim()) return;
    
    setSendingReply(true);
    try {
      const response = await client.api.fetch(`/api/admin/support-requests/${selectedRequest.id}/reply`, {
        method: 'POST',
        body: JSON.stringify({ reply: replyText }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Uspešno', 'Odgovor je poslat korisniku');
        setReplyText('');
        fetchRequests();
        setSelectedRequest(prev => prev ? { 
          ...prev, 
          status: 'resolved', 
          adminReply: replyText,
          repliedAt: Math.floor(Date.now() / 1000)
        } : null);
      } else {
        toast.error('Greška', data.error || 'Greška pri slanju odgovora');
      }
    } catch (error) {
      toast.error('Greška', 'Greška pri slanju odgovora');
    } finally {
      setSendingReply(false);
    }
  };

  const isDeletionRequest = (req: SupportRequest) => req.message.startsWith('[BRISANJE NALOGA]');

  const handleDeactivateUser = async (userId: number) => {
    if (!confirm('Da li ste sigurni da želite da deaktivirate ovaj korisnički nalog? Ova akcija će suspendovati korisnika i sve njegove oglase.')) return;
    
    setDeactivating(true);
    try {
      const response = await client.api.fetch(`/api/admin/users/${userId}/deactivate`, {
        method: 'POST',
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Uspešno', data.message || 'Korisnik je deaktiviran');
        fetchRequests();
        if (selectedRequest) {
          handleUpdateStatus(selectedRequest.id, 'closed');
        }
      } else {
        toast.error('Greška', data.error || 'Greška pri deaktivaciji korisnika');
      }
    } catch (error) {
      toast.error('Greška', 'Greška pri deaktivaciji korisnika');
    } finally {
      setDeactivating(false);
    }
  };

  const formatDate = (timestamp: number) => {
    const d = new Date(timestamp * 1000);
    return d.toLocaleDateString('sr-Latn-RS', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const totalCount = counts.new + counts.in_progress + counts.resolved + counts.closed;

  return (
    <div className="space-y-6">
      {/* Status summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { key: 'all', label: 'Ukupno', count: totalCount, color: 'text-blue-500' },
          { key: 'new', label: 'Novi', count: counts.new, color: 'text-red-500' },
          { key: 'in_progress', label: 'U toku', count: counts.in_progress, color: 'text-amber-500' },
          { key: 'resolved', label: 'Rešeni', count: counts.resolved, color: 'text-green-500' },
          { key: 'closed', label: 'Zatvoreni', count: counts.closed, color: 'text-slate-500' },
        ].map(({ key, label, count, color }) => (
          <button
            key={key}
            onClick={() => setStatusFilter(key)}
            className={`p-4 rounded-xl text-center transition-all ${
              statusFilter === key
                ? 'ring-2 ring-amber-500 shadow-lg'
                : 'hover:shadow-md'
            } bg-white dark:bg-slate-800`}
          >
            <div className={`text-2xl font-bold ${color}`}>
              {count}
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">{label}</div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Pretraži po imenu, email-u ili poruci..."
          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
        />
      </div>

      {/* Request list + Detail panel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Request list */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Zahtevi {statusFilter !== 'all' && `(${statusLabels[statusFilter]})`}
          </h3>
          
          {loading ? (
            <div className="text-center py-12 text-slate-500">
              <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              Učitavanje...
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl">
              <svg className="w-12 h-12 mx-auto mb-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-slate-500 dark:text-slate-400">Nema zahteva za podršku</p>
            </div>
          ) : (
            requests.map((req) => (
              <div
                key={req.id}
                onClick={() => {
                  setSelectedRequest(req);
                  setReplyText('');
                  setShowChatHistory(false);
                }}
                className={`p-4 rounded-xl cursor-pointer transition-all border-2 ${
                  selectedRequest?.id === req.id
                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                    : 'border-transparent bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[req.status]}`}>
                        {statusLabels[req.status]}
                      </span>
                      <span className="text-xs text-slate-500">#{req.id}</span>
                    </div>
                    <h4 className="font-medium text-slate-900 dark:text-white truncate">
                      {req.userName}
                    </h4>
                    <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                      {req.userEmail || 'Bez email-a'}
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mt-1 line-clamp-2">
                      {req.message}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs text-slate-500">
                      {formatDate(req.createdAt)}
                    </div>
                    {req.status === 'new' && (
                      <div className="w-2 h-2 bg-red-500 rounded-full mt-2 ml-auto"></div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Right: Detail panel */}
        <div className="lg:sticky lg:top-4">
          {selectedRequest ? (
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Zahtev #{selectedRequest.id}
                  </h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[selectedRequest.status]}`}>
                    {statusLabels[selectedRequest.status]}
                  </span>
                </div>
                <div className="space-y-1 text-sm">
                  <p className="text-slate-600 dark:text-slate-400">
                    <span className="font-medium text-slate-900 dark:text-white">Korisnik:</span>{' '}
                    {selectedRequest.userName}
                  </p>
                  <p className="text-slate-600 dark:text-slate-400">
                    <span className="font-medium text-slate-900 dark:text-white">Email:</span>{' '}
                    {selectedRequest.userEmail || 'Nije dostupan'}
                  </p>
                  <p className="text-slate-600 dark:text-slate-400">
                    <span className="font-medium text-slate-900 dark:text-white">Stranica:</span>{' '}
                    {selectedRequest.page || 'Nepoznato'}
                  </p>
                  <p className="text-slate-600 dark:text-slate-400">
                    <span className="font-medium text-slate-900 dark:text-white">Datum:</span>{' '}
                    {formatDate(selectedRequest.createdAt)}
                  </p>
                </div>
              </div>

              {/* Message */}
              <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Poruka korisnika:</h4>
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                  <p className="text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{selectedRequest.message}</p>
                </div>
              </div>

              {/* Chat History */}
              {selectedRequest.chatHistory && (
                <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                  <button
                    onClick={() => setShowChatHistory(!showChatHistory)}
                    className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:text-amber-500 transition-colors"
                  >
                    <svg className={`w-4 h-4 transition-transform ${showChatHistory ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Istorija razgovora sa AI
                  </button>
                  {showChatHistory && (
                    <div className="mt-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <pre className="text-xs text-slate-600 dark:text-slate-400 whitespace-pre-wrap font-mono">
                        {selectedRequest.chatHistory}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {/* Admin Reply (if exists) */}
              {selectedRequest.adminReply && (
                <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                  <h4 className="text-sm font-semibold text-green-700 dark:text-green-400 mb-2">
                    Odgovor admina {selectedRequest.adminName && `(${selectedRequest.adminName})`}:
                  </h4>
                  <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 rounded-r-lg p-4">
                    <p className="text-slate-800 dark:text-slate-200 whitespace-pre-wrap">{selectedRequest.adminReply}</p>
                    {selectedRequest.repliedAt && (
                      <p className="text-xs text-slate-500 mt-2">{formatDate(selectedRequest.repliedAt)}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Status Actions */}
              <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Promeni status:</h4>
                <div className="flex flex-wrap gap-2">
                  {(['new', 'in_progress', 'resolved', 'closed'] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => handleUpdateStatus(selectedRequest.id, s)}
                      disabled={selectedRequest.status === s}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        selectedRequest.status === s
                          ? 'ring-2 ring-amber-500 ' + statusColors[s]
                          : statusColors[s] + ' opacity-60 hover:opacity-100'
                      }`}
                    >
                      {statusLabels[s]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Account Deletion Action */}
              {isDeletionRequest(selectedRequest) && selectedRequest.userId && selectedRequest.status !== 'closed' && (
                <div className="p-6 border-b border-slate-200 dark:border-slate-700">
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-2 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      Zahtev za brisanje naloga
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                      Odobravanjem ovog zahteva, korisnički nalog će biti deaktiviran i svi aktivni oglasi suspendovani.
                    </p>
                    <button
                      onClick={() => handleDeactivateUser(selectedRequest.userId!)}
                      disabled={deactivating}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      {deactivating ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                          Deaktiviram...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                          </svg>
                          Odobri brisanje i deaktiviraj nalog
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Reply Form */}
              {selectedRequest.status !== 'closed' && (
                <div className="p-6">
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    {selectedRequest.adminReply ? 'Pošalji novi odgovor:' : 'Odgovori korisniku:'}
                  </h4>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Unesite odgovor koji će biti poslat korisniku putem email-a i notifikacije..."
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                  />
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xs text-slate-500">
                      {selectedRequest.userEmail 
                        ? `Odgovor će biti poslat na: ${selectedRequest.userEmail}`
                        : 'Korisnik nema email — odgovor će biti samo u notifikacijama'}
                    </p>
                    <button
                      onClick={handleSendReply}
                      disabled={sendingReply || !replyText.trim()}
                      className="px-6 py-2 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      {sendingReply ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                          Šaljem...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                          Pošalji odgovor
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-12 text-center">
              <svg className="w-16 h-16 mx-auto mb-4 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <p className="text-slate-500 dark:text-slate-400">Izaberite zahtev sa leve strane za pregled detalja</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
